import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { Brand } from '@/data/types';

// New chat (Gary, 15 Oct): find a Sentinel customer or lead, pick an approved chat starter,
// send it through messaging-send (start_chat). The server checks access, opt-outs and Meta's
// approval again; this only gathers the choices.

/**
 * kind 'chat': restarting an existing chat whose 24 hours are up (id is the chat).
 * kind 'number': a typed number not found in Sentinel (Gary, 16 Oct); the server checks the
 * whole country and makes a lead when it is new. firstName is typed by the sender.
 */
export interface Who { kind: 'customer' | 'lead' | 'chat' | 'number'; id: string; name: string; firstName: string; phone: string | null }

/** sender: {{2}} is the sender's own first name, filled in by the server. */
export interface Starter { name: string; body: string; places: string[]; sender: boolean }

/** The approved messages that start a chat, with labels for the details typed after the name. */
const STARTERS: Record<string, { title: string; places: string[]; sender?: boolean; replaces?: string }> = {
  // The service versions take over once Meta approves them (Gary, 19 Oct: the marketing ones were held back).
  cmb_intro_after_call: { title: 'After a call', places: [], sender: true, replaces: 'cmb_intro_call' },
  cmb_intro_call: { title: 'After a call', places: [], sender: true },
  cmb_intro_referral: { title: 'Number passed on', places: ['Who passed it on, e.g. Your sister Jane'], sender: true },
  cmb_enquiry_update: { title: 'Follow up an enquiry', places: [], replaces: 'cmb_enquiry_followup' },
  cmb_enquiry_followup: { title: 'Follow up an enquiry', places: [] },
  cmb_booking_reminder_v2: { title: 'Booking reminder', places: ['Day, e.g. Thursday 17 October', 'Time, e.g. 10:00'] },
  cmb_booking_contact: { title: 'About their booking', places: ['Day, e.g. Thursday 17 October'] },
  cmb_service_due: { title: 'Next service due', places: [] },
  // Peace of Sleep, sent from the POS number (Charlie's wording, 3 Nov 2026).
  pos_intro_after_call: { title: 'After a call', places: [] },
  pos_intro_referral: { title: 'Number passed on', places: ['Who passed it on, e.g. Tintswalo'] },
  pos_enquiry_followup: { title: 'Follow up an enquiry', places: ['Property, e.g. Oak Lodge'] },
  pos_proposal_followup: { title: 'Proposal follow-up', places: ['Property, e.g. Oak Lodge'] },
  pos_service_reminder: { title: 'Service reminder', places: ['Property, e.g. Oak Lodge', 'Day and time, e.g. Thursday 17 October at 09:00'] },
  pos_service_due: { title: 'Next service due', places: ['Property, e.g. Oak Lodge'] },
};

/** Which numbers can start a chat today: Peace of Sleep only once its number is connected. */
export async function startOptions(): Promise<{ pos: boolean }> {
  const { data, error } = await supabase.functions.invoke('messaging-send', { body: { action: 'start_options' } });
  return { pos: !error && (data as { pos?: boolean } | null)?.pos === true };
}

/** The digits of a typed phone number, or null when the text is not one (9 digits or more). */
export function typedNumber(term: string): string | null {
  const t = term.trim();
  if (!/^[+\d\s()-]+$/.test(t)) return null;
  const digits = t.replace(/\D/g, '');
  return digits.length >= 9 ? digits : null;
}

export function starterTitle(name: string): string {
  return STARTERS[name]?.title ?? name;
}

const nameOf = (r: { first_name?: string | null; last_name?: string | null; customer_name?: string | null }) =>
  [r.first_name, r.last_name].filter(Boolean).join(' ').trim() || r.customer_name || 'No name';

/** Customers and leads matching a name or number. Row rules decide what this login sees.
 *  Peace of Sleep: its clients only (POS enquiries are not leads). */
export async function findPeople(term: string, brand: Brand = 'cmb'): Promise<Who[]> {
  const q = term.trim();
  if (q.length < 2) return [];
  const digits = q.replace(/\D/g, '');
  const like = `%${q.replace(/[%_,()]/g, ' ')}%`;
  const numberLike = digits.length >= 3 ? `%${digits.slice(-9)}%` : null;
  const custFilter = numberLike
    ? `mobile.ilike.${numberLike},phone.ilike.${numberLike}`
    : `first_name.ilike.${like},last_name.ilike.${like},customer_name.ilike.${like}`;
  const leadFilter = numberLike ? `phone.ilike.${numberLike}` : `first_name.ilike.${like},last_name.ilike.${like}`;
  const customers = supabase.from('customers').select('id, first_name, last_name, customer_name, mobile, phone').or(custFilter);
  const [c, l] = await Promise.all([
    (brand === 'pos' ? customers.eq('is_pos', true) : customers).limit(15),
    brand === 'pos' ? Promise.resolve({ data: [] }) : supabase.from('leads').select('id, first_name, last_name, phone, customer_id').or(leadFilter).is('customer_id', null).limit(15),
  ]);
  const people: Who[] = [];
  for (const r of (c.data ?? []) as { id: string; first_name: string | null; last_name: string | null; customer_name: string | null; mobile: string | null; phone: string | null }[]) {
    people.push({ kind: 'customer', id: r.id, name: nameOf(r), firstName: (r.first_name ?? nameOf(r)).split(' ')[0] ?? '', phone: r.mobile || r.phone });
  }
  for (const r of (l.data ?? []) as { id: string; first_name: string | null; last_name: string | null; phone: string | null }[]) {
    people.push({ kind: 'lead', id: r.id, name: nameOf(r), firstName: (r.first_name ?? '').split(' ')[0] ?? '', phone: r.phone });
  }
  return people;
}

/** The chat starters Meta has approved, with their wording. */
export async function approvedStarters(brand: Brand = 'cmb'): Promise<Starter[]> {
  const { data } = await supabase.from('messaging_templates')
    .select('name, components').eq('provider', 'whatsapp').eq('brand', brand).eq('status', 'APPROVED').in('name', Object.keys(STARTERS));
  return ((data ?? []) as { name: string; components: { type?: string; text?: string }[] | null }[])
    .map((t) => ({
      name: t.name, body: (t.components ?? []).find((c) => c.type === 'BODY')?.text ?? '',
      places: STARTERS[t.name]?.places ?? [], sender: STARTERS[t.name]?.sender === true,
    }))
    .sort((a, b) => Object.keys(STARTERS).indexOf(a.name) - Object.keys(STARTERS).indexOf(b.name))
    .filter((s, _i, all) => !all.some((o) => STARTERS[o.name]?.replaces === s.name));
}

/** The wording with the name and typed details in place, as the customer will see it. */
export function previewText(body: string, values: string[]): string {
  return body.replace(/\{\{(\d+)\}\}/g, (_, i: string) => values[Number(i) - 1] || '…');
}

export async function startChat(who: Who, template: string, details: string[], brand: Brand = 'cmb'): Promise<{ id: string } | { error: string }> {
  const { data, error } = await supabase.functions.invoke('messaging-send', {
    body: who.kind === 'number'
      ? { action: 'start_chat', brand, phone: who.phone, first_name: who.firstName, template, params: details }
      : { action: 'start_chat', brand, [who.kind === 'customer' ? 'customer_id' : who.kind === 'lead' ? 'lead_id' : 'conversation_id']: who.id, template, params: details },
  });
  if (error) {
    if (error instanceof FunctionsHttpError) {
      try {
        const body = await error.context.json() as { error?: string };
        if (body.error) return { error: body.error };
      } catch { /* not JSON */ }
    }
    return { error: 'Something went wrong. Try again.' };
  }
  return { id: (data as { conversation_id: string }).conversation_id };
}
